import { useCallback, useEffect, useRef, useState } from "react";
import {
  useSubscribePush,
  useVapidPublicKey,
} from "../../../entities/notification";
import { isIos, isStandalone } from "../../../shared/lib/pwa";
import {
  createLocalSubscription,
  getLocalSubscription,
  isPushSupported,
  readPermission,
  requestPushPermission,
} from "../../../shared/lib/push";

export type PushState =
  /** No service worker, no PushManager, or an insecure context. Show nothing. */
  | "unsupported"
  /** iOS only exposes PushManager to an installed app. The install banner makes that case. */
  | "ios-not-installed"
  | "default"
  | "requesting"
  | "subscribed"
  /** Browser permission is granted; the registrar is (re)subscribing silently. Nothing to ask. */
  | "granted"
  /** Permission was granted but subscribing or registering failed — retryable. */
  | "error"
  /** Irreversible from our side: only the user can undo it in site settings. */
  | "denied";

type Options = {
  /**
   * Re-post the current subscription on mount, without ever prompting.
   *
   * This is the practical answer to `pushsubscriptionchange`, which browsers fire
   * inconsistently and which the service worker could not authenticate anyway: the endpoint is
   * unique, so re-posting is an idempotent upsert that costs one request and silently repairs
   * a rotated subscription.
   */
  silentRegisterOnMount?: boolean;
};

/**
 * Deliberately minimal since the burger-menu entry was removed: the only user-facing surface
 * left is the one-shot banner (state + enable), plus the silent re-registration the registrar
 * runs. Switching a device off is an admin act on the Einstellungen → Push board; a test send
 * is the same board's send-to-self.
 */
export function usePushNotifications(options: Options = {}) {
  const supported = isPushSupported();
  const iosNotInstalled = isIos() && !isStandalone();
  const usable = supported && !iosNotInstalled;

  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    readPermission,
  );
  const [hasLocalSubscription, setHasLocalSubscription] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Only fetched once push could actually be used: a denied browser will never subscribe, and
  // on a deployment with push switched off this endpoint answers 503.
  const vapidKey = useVapidPublicKey({ enabled: usable && permission === "granted" });

  const subscribe = useSubscribePush();

  useEffect(() => {
    if (!usable) return;
    let cancelled = false;
    void getLocalSubscription().then((subscription) => {
      if (!cancelled) setHasLocalSubscription(subscription !== null);
    });
    return () => {
      cancelled = true;
    };
  }, [usable]);

  const registerSilently = useRef(false);
  useEffect(() => {
    if (!options.silentRegisterOnMount) return;
    if (!usable || permission !== "granted") return;
    if (!vapidKey.data?.publicKey) return;
    if (registerSilently.current) return;

    registerSilently.current = true;
    void (async () => {
      try {
        const dto = await createLocalSubscription(vapidKey.data.publicKey);
        await subscribe.mutateAsync(dto);
        setHasLocalSubscription(true);
      } catch {
        // Silent on purpose: this path never prompted, so there is no user action to report a
        // failure against. The next app open tries again.
      }
    })();
    // `subscribe` is a stable-enough mutation object; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.silentRegisterOnMount, usable, permission, vapidKey.data?.publicKey]);

  /**
   * Must be called from a user gesture — iOS refuses otherwise, and Chrome penalises an origin
   * that prompts on load. That is the whole reason this is a callback and not an effect.
   */
  const enable = useCallback(async () => {
    if (!usable) return;

    setBusy(true);
    setFailed(false);
    try {
      const granted = await requestPushPermission();
      setPermission(granted);
      if (granted !== "granted") return;

      const key = vapidKey.data?.publicKey ?? (await vapidKey.refetch()).data?.publicKey;
      if (!key) {
        setFailed(true);
        return;
      }

      const dto = await createLocalSubscription(key);
      await subscribe.mutateAsync(dto);
      setHasLocalSubscription(true);
    } catch {
      // Permission may well be granted while subscribing failed — offline, or the push service
      // was unreachable. Retrying is then a single tap, so say so rather than going quiet.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [usable, vapidKey, subscribe]);

  const state: PushState = !supported
    ? "unsupported"
    : iosNotInstalled
    ? "ios-not-installed"
    : busy
    ? "requesting"
    : permission === "denied"
    ? "denied"
    : permission === "granted" && hasLocalSubscription
    ? "subscribed"
    : failed
    ? "error"
    : permission === "granted"
    ? "granted"
    : "default";

  return { state, enable };
}
